import {
  createTemplateTheme,
  type CodeComponentTemplateBranding,
} from './branding';

export function createQuotesCarouselSource(
  branding: CodeComponentTemplateBranding = {}
): string {
  const {themeSource, primaryColor} = createTemplateTheme(branding, '#2563eb');

  return `import { declareComponent } from "@webflow/react";
import { props } from "@webflow/data-types";
import { useCallback, useEffect, useRef, useState } from "react";

${themeSource}

/* ── testimonials ── */
const QUOTES = [
  {
    text: "We shipped our new pricing page in an afternoon instead of the usual two-week cycle.",
    name: "Jordan Mackey",
    role: "Head of Growth, Fielder",
  },
  {
    text: "The visual editor is the first AI tool our whole marketing team actually adopted.",
    name: "Renata Salvi",
    role: "VP Marketing, Northloop",
  },
  {
    text: "Support tickets about broken layouts dropped to almost zero once components carried their own states.",
    name: "Devon Kwan",
    role: "Eng Manager, Basecamp Labs",
  },
  {
    text: "We onboarded a contractor on Monday and she was publishing pages by Wednesday.",
    name: "Amara Thorne",
    role: "Founder, Loop & Co.",
  },
  {
    text: "Design review moved from a weekly sync to a comment thread. Rare that a tool cuts meetings.",
    name: "Priya Lall",
    role: "Product Design Lead, Verto",
  },
];

const N = QUOTES.length;
// The last card is cloned before the first and the first after the last, so a
// neighbor always peeks on both sides and the loop keeps moving forward.
const SLIDES = [QUOTES[N - 1], ...QUOTES, QUOTES[0]];

const AUTOPLAY_MS = 5000;
const SLIDE_MS = 500;

// Shadow DOM ignores Tailwind's @property defaults: set border and outline
// styles explicitly and avoid shadow or ring utilities.
const CARD =
  "w-[min(72vw,28rem)] flex-none border border-solid p-9 text-left max-[768px]:p-8 " +
  FOCUS_RING;
const DOT = "h-1.5 w-1.5 rounded-full transition " + FOCUS_RING;
const ARROW_BUTTON =
  "flex h-9 w-9 items-center justify-center rounded-full border border-solid border-[color:var(--template-border)] bg-[var(--template-surface)] text-[color:var(--template-ink)] hover:bg-[var(--template-secondary)] " +
  FOCUS_RING;

function Chevron({ dir }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      {dir === "left" ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
  );
}

/* ── main component ── */
export function QuotesCarousel({ primaryColor = ${primaryColor} }) {
  // Position in SLIDES; 0 and N + 1 are the clones.
  const [slide, setSlide] = useState(1);
  const [animate, setAnimate] = useState(true);
  const [offset, setOffset] = useState(0);
  const index = (slide - 1 + N) % N;
  // Hover and focus pause autoplay separately, so moving the pointer away
  // never resumes it while focus is still inside.
  const [hovered, setHovered] = useState(false);
  const [focusedInside, setFocusedInside] = useState(false);

  const viewportRef = useRef(null);
  const cardRefs = useRef([]);
  const touchStartX = useRef(null);

  const next = useCallback(() => setSlide((s) => Math.min(s + 1, N + 1)), []);
  const prev = useCallback(() => setSlide((s) => Math.max(s - 1, 0)), []);

  // Once a clone slides in, jump to its real card without animating.
  useEffect(() => {
    if (slide !== 0 && slide !== N + 1) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(() => {
      setAnimate(false);
      setSlide(slide === 0 ? N : 1);
    }, reduceMotion ? 0 : SLIDE_MS);
    return () => clearTimeout(timer);
  }, [slide]);

  useEffect(() => {
    if (animate) return;
    const timer = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(timer);
  }, [animate]);

  // Center the active card
  useEffect(() => {
    const viewport = viewportRef.current;
    const card = cardRefs.current[slide];
    if (!viewport || !card) return;

    const measure = () => {
      setOffset(card.offsetLeft + card.offsetWidth / 2 - viewport.clientWidth / 2);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [slide]);

  // Keep focus on the active card so a focused card is never aria-hidden.
  useEffect(() => {
    const viewport = viewportRef.current;
    const card = cardRefs.current[slide];
    const focused = viewport?.getRootNode().activeElement;
    if (card && focused && focused !== viewport && viewport.contains(focused)) {
      card.focus({ preventScroll: true });
    }
  }, [slide]);

  // Autoplay (respect reduced motion)
  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (hovered || focusedInside || reduceMotion) return;
    const timer = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [hovered, focusedInside, slide, next]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="mx-auto w-full max-w-3xl"
      style={themeStyle(primaryColor)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocusedInside(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocusedInside(false);
      }}
    >
      <div
        ref={viewportRef}
        tabIndex={0}
        aria-label="Testimonials"
        className={"overflow-hidden py-3 " + FOCUS_RING}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") prev();
          if (e.key === "ArrowRight") next();
        }}
      >
        <div
          className={
            animate
              ? "flex items-stretch gap-5 transition-transform duration-500 ease-[cubic-bezier(0.65,0,0.35,1)] motion-reduce:transition-none"
              : "flex items-stretch gap-5 transition-none"
          }
          style={{ transform: "translateX(" + -offset + "px)" }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {SLIDES.map((q, i) => {
            const active = i === slide;
            const look = active
              ? " scale-100 border-[color:var(--template-secondary)] bg-[var(--template-surface)] opacity-100"
              : " scale-[0.92] border-[color:var(--template-border)] bg-[color-mix(in_srgb,var(--template-accent)_6%,var(--template-surface))] opacity-40";
            return (
              <button
                key={i}
                type="button"
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                onClick={() => setSlide(i)}
                aria-current={active}
                // Keyboard and screen reader users move with the arrows and dots.
                tabIndex={active ? 0 : -1}
                aria-hidden={!active}
                className={
                  CARD +
                  look +
                  (animate
                    ? " transition duration-500 motion-reduce:transition-none"
                    : " transition-none")
                }
                style={{ boxShadow: active ? "0 1px 2px 0 rgb(0 0 0 / 0.05)" : "none" }}
              >
                {/* 🎨 BRAND COLOR: Quote mark */}
                <span
                  className="mb-1.5 block font-serif text-4xl leading-none text-[color:var(--template-accent-secondary)]"
                  aria-hidden="true"
                >
                  “
                </span>
                {/* QUOTE - Size: lg, Weight: 400 */}
                <span className="mb-6 block text-lg text-[color:var(--template-ink)]">{q.text}</span>
                <span className="flex flex-col gap-0.5">
                  {/* NAME - Size: base, Weight: 600 */}
                  <span className={TYPE.h3}>
                    {q.name}
                  </span>
                  {/* ROLE - Size: sm, Weight: 400 */}
                  <span className={TYPE.small}>{q.role}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-center gap-5">
        <button
          type="button"
          aria-label="Previous testimonial"
          onClick={prev}
          className={ARROW_BUTTON}
        >
          <Chevron dir="left" />
        </button>

        {/* 🎨 BRAND COLOR: Active dot */}
        <div className="flex gap-1.5" role="group" aria-label="Choose testimonial">
          {QUOTES.map((q, i) => (
            <button
              key={q.name}
              type="button"
              aria-label={"Go to testimonial " + (i + 1)}
              aria-current={i === index}
              onClick={() => setSlide(i + 1)}
              className={
                i === index
                  ? DOT + " bg-[var(--template-accent)]"
                  : DOT + " bg-[var(--template-border-strong)] hover:bg-[color-mix(in_srgb,var(--template-accent)_50%,var(--template-surface))]"
              }
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="Next testimonial"
          onClick={next}
          className={ARROW_BUTTON}
        >
          <Chevron dir="right" />
        </button>
      </div>
    </div>
  );
}

export default declareComponent(QuotesCarousel, {
  name: "Quotes Carousel",
  description: "Testimonial carousel with autoplay, arrow controls, and dot navigation.",
  group: "Sections",
  props: {
    primaryColor: props.Text({
      name: "Primary Brand Color",
      defaultValue: ${primaryColor},
    }),
  },
});
`;
}

export const QUOTES_CAROUSEL_SOURCE = createQuotesCarouselSource();
